import fetchRetry from "fetch-retry";
import {
  CONVERSATION_BUSY_STATUS,
  safeFetchStreaming as sharedFetchStreaming,
  parseRetryAfterMs,
} from "@common/utils/streamingFetch";
import { makeHttpError } from "@common/utils/http-error";

const fetchWithRetry = fetchRetry(global.fetch);

// Back off by the server's Retry-After when present, otherwise exponential.
function retryDelayWithRetryAfter(attempt: number, _error: Error | null, response: Response | null): number {
  const backoff = Math.pow(2, attempt) * 100;
  const retryAfterMs = parseRetryAfterMs(response);
  return retryAfterMs !== null ? Math.max(retryAfterMs, backoff) : backoff;
}

const DEFAULT_RETRY_STATUSES = [408, 429, 502, 503, 504];

export async function safeFetch(
  url: string,
  options: RequestInit = {},
  retryConfig: { retryStatuses?: number[]; retryOnNetworkError?: boolean } = {},
): Promise<Response> {
  const retryStatuses = retryConfig.retryStatuses ?? DEFAULT_RETRY_STATUSES;
  const retryOnNetworkError = retryConfig.retryOnNetworkError ?? true;
  const response = await fetchWithRetry(url, {
    ...options,
    retries: 5,
    retryDelay: retryDelayWithRetryAfter,
    retryOn: function(attempt, error, response) {
      if (attempt >= 5) return false;
      // Retry on network-level failures (no response received):
      // - Chrome/Edge: "Failed to fetch"
      // - Safari: "Load failed"
      // But not AbortError which is an intentional cancellation.
      if (response === null) return retryOnNetworkError && error?.name !== 'AbortError';
      // Retry on transient HTTP errors (callers may narrow this set, e.g. the ICI
      // proxy hop drops 502 since the proxy already exhausted the upstream call).
      return retryStatuses.includes(response.status);
    },
    headers: (() => {
      // Safely handle HeadersInit (can be Headers, object, or string[][])
      const headers = new Headers(options.headers);

      // Only set defaults when missing (don't override existing headers)
      if (!headers.has('Accept')) {
        headers.set('Accept', '*/*');
      }
      // Don't set Accept-Encoding - it's forbidden in browsers and handled automatically

      return headers;
    })()
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "");
    throw makeHttpError(response.status, errorBody);
  }

  return response;
}

export async function safeFetchStreaming(url: string, options: RequestInit = {}): Promise<Response> {
  return sharedFetchStreaming(url, options, [...DEFAULT_RETRY_STATUSES, CONVERSATION_BUSY_STATUS]);
}

export async function safeFetchJSON<T = any>(url: string, options: RequestInit = {}): Promise<T | undefined> {
  const response = await safeFetch(url, options);

  try {
    const text = await response.text();
    if (!text.trim()) {
      return undefined;
    }

    return JSON.parse(text) as T;
  } catch (error) {
    throw new Error(`Failed to parse JSON response: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}